/**
 * Zustand store for managing backtest state.
 * Provides global state for the current backtest result and UI state.
 */

import { create } from 'zustand';
import type { ApiBacktestResult, AggregateBacktestResult } from '../types';

// ============================================================================
// Backtest Store
// ============================================================================

interface BacktestStore {
  // Current backtest result
  currentResult: ApiBacktestResult | AggregateBacktestResult | null;

  // UI state
  isRunning: boolean;
  error: string | null;

  // Selected backtest from history
  selectedBacktestId: string | null;

  /**
   * Id of a backtest that failed to load because it no longer exists
   * (e.g. deleted, or a stale deep-link). Distinct from `error` (a generic
   * message) so the UI can render a dedicated "not found" state instead of
   * a blank panel. Cleared whenever a result loads successfully.
   */
  runNotFoundId: string | null;

  // Actions
  setResult: (result: ApiBacktestResult | AggregateBacktestResult) => void;
  setRunning: (running: boolean) => void;
  setError: (error: string | null) => void;
  setSelectedBacktestId: (id: string | null) => void;
  setRunNotFound: (id: string | null) => void;
  clear: () => void;
}

export const useBacktestStore = create<BacktestStore>((set) => ({
  // Initial state
  currentResult: null,
  isRunning: false,
  error: null,
  selectedBacktestId: null,
  runNotFoundId: null,

  // Actions
  setResult: (result) =>
    set({
      currentResult: result,
      isRunning: false,
      error: null,
      selectedBacktestId: result.id,
      runNotFoundId: null,
    }),

  setRunning: (running) =>
    set({
      isRunning: running,
      error: running ? null : undefined, // Clear error when starting
    }),

  setError: (error) =>
    set({
      error,
      isRunning: false,
    }),

  setSelectedBacktestId: (id) =>
    set({
      selectedBacktestId: id,
    }),

  setRunNotFound: (id) =>
    set({
      runNotFoundId: id,
    }),

  clear: () =>
    set({
      currentResult: null,
      isRunning: false,
      error: null,
      selectedBacktestId: null,
      runNotFoundId: null,
    }),
}));

// Re-export from split store files for backward compatibility
export { useConfigStore } from './configStore';
export { useOptimizationStore } from './optimizationStore';
export { useOptimizerModalStore } from './optimizerModalStore';
